import type { FC, ReactNode } from 'react'

import { Logo } from '@/components/generals/site-header'
import { PixelArrow } from '@/components/generals/pixel-button'

import PixelEdge from '@/components/pixel/pixel-edge'

import { NAV_LINKS, SERVICES } from '@/constants/content'

const WORDMARK = 'dinolab'

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative bg-ink text-paper'>
            <PixelEdge color='var(--ink)' seed={42} className='absolute inset-x-0 bottom-[calc(100%-1px)]' />

            <div className='mx-auto grid max-w-7xl gap-12 px-5 pt-16 pb-12 sm:grid-cols-2 md:px-8 md:pt-20 lg:grid-cols-[1.5fr_1fr_1fr_1.2fr]'>
                <div>
                    <a href='#hero' className='group inline-block' aria-label="dinolab, back to top">
                        <Logo dark />
                    </a>

                    <p className='mt-5 max-w-xs text-sm leading-relaxed text-paper/70'>
                        Digital marketing and creative solutions for brands that refuse to blend into the background.
                    </p>
                </div>

                <nav aria-label="Footer">
                    <h2 className='font-heading text-lg text-bubblegum'>
                        explore
                    </h2>

                    <ul className='mt-4 space-y-2.5'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <a href={link.href} className='text-sm font-semibold text-paper/80 underline-offset-4 hover:text-paper hover:underline'>
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div>
                    <h2 className='font-heading text-lg text-bubblegum'>
                        services
                    </h2>

                    <ul className='mt-4 space-y-2.5'>
                        {SERVICES.map(service => (
                            <li key={service.id}>
                                <a href='#services' className='text-sm font-semibold text-paper/80 underline-offset-4 hover:text-paper hover:underline'>
                                    {service.title}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h2 className='font-heading text-lg text-bubblegum'>
                        say hi
                    </h2>

                    <p className='mt-4 max-w-xs text-sm leading-relaxed text-paper/70'>
                        Got a project in mind? The quickest way to reach the crew is the project form. We reply within one business day.
                    </p>

                    <a
                        href='#contacts'
                        className='group/button mt-5 inline-flex items-center gap-2 font-heading text-lg text-paper underline decoration-bubblegum decoration-2 underline-offset-[6px] hover:text-bubblegum'
                    >
                        start a project
                        <PixelArrow />
                    </a>
                </div>
            </div>

            <p aria-hidden='true' className='overflow-hidden px-3 text-center font-heading text-[clamp(4.5rem,22vw,20rem)] leading-[0.8] whitespace-nowrap text-bubblegum select-none'>
                {Array.from(WORDMARK).map((letter, index) => (
                    <span
                        key={index}
                        className='inline-block transition-[translate,color] duration-150 ease-[steps(2,end)] hover:-translate-y-[0.08em] hover:text-fern'
                    >
                        {letter}
                    </span>
                ))}
            </p>

            <div className='border-t-2 border-paper/15'>
                <div className='mx-auto flex max-w-7xl flex-col gap-3 px-5 py-6 text-xs font-medium text-paper/60 md:flex-row md:items-center md:justify-between md:px-8'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> dinolab. All rights reserved.
                    </p>

                    <p>
                        Made with pixels, coffee and a lot of rawr.
                    </p>

                    <a href='#hero' className='group/button inline-flex items-center gap-2 font-heading text-sm text-paper hover:text-bubblegum'>
                        back to top
                        <PixelArrow className='-rotate-90' />
                    </a>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
